import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Pencil, Trash2, Check, X, Plus, User, Shield, Plug, Database, Mail, Save, ChevronRight, Link2, Link2Off } from 'lucide-react';
import { refreshFuelBrands } from '../hooks/useFuelBrands.js';
import { refreshPosTypes } from '../hooks/usePosTypes.js';
import { useAuth } from '../context/AuthContext.jsx';
import Badge, { PosBadge } from '../components/Badge.jsx';

// ---- Sidebar nav definition ---------------------------------------
function navItems(isAdmin) {
  const items = [
    { id: 'account',        label: 'Account',         icon: User },
  ];
  if (isAdmin) {
    items.push(
      { id: 'administration', label: 'Administration', icon: Shield },
      { id: 'integrations',   label: 'Integrations',   icon: Plug },
      { id: 'data',           label: 'Data Lists',     icon: Database },
    );
  }
  return items;
}

export default function Settings() {
  const { user } = useAuth();
  const items = navItems(!!user?.is_admin);
  // If the user is returning from the QBO OAuth flow (?qbo=connected or
  // ?qbo=denied), land them on the Integrations tab so they see the
  // result instead of the Account tab.
  const initialActive = (() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('qbo') && items.some((it) => it.id === 'integrations')) {
      return 'integrations';
    }
    return items[0].id;
  })();
  const [active, setActive] = useState(initialActive);

  return (
    <section className="page settings-page">
      <div className="page-header">
        <h2>Settings</h2>
        <div className="subtitle">Configure XSite Command</div>
      </div>

      <div className="settings-layout">
        <nav className="settings-sidebar" aria-label="Settings categories">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <button
                key={it.id}
                type="button"
                className={`settings-nav-item${active === it.id ? ' settings-nav-item-active' : ''}`}
                onClick={() => setActive(it.id)}
              >
                <Icon size={16} strokeWidth={1.75} />
                <span>{it.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="settings-content">
          {active === 'account'        && <AccountPanel user={user} />}
          {active === 'administration' && <AdministrationPanel />}
          {active === 'integrations'   && <IntegrationsPanel />}
          {active === 'data'           && <DataListsPanel />}
        </div>
      </div>
    </section>
  );
}

// ---- Account ------------------------------------------------------
function AccountPanel({ user }) {
  return (
    <>
      <SettingsCard
        title="Your Profile"
        subtitle="Your XSite Command identity. To change name or email, ask an administrator."
      >
        <dl className="settings-profile">
          <dt>Name</dt><dd>{user?.name || '—'}</dd>
          <dt>Email</dt><dd className="cell-mono">{user?.email || '—'}</dd>
          <dt>Role</dt><dd>{user?.is_admin ? 'Administrator' : 'User'}</dd>
        </dl>
      </SettingsCard>

      <EmailSignatureEditor />
    </>
  );
}

function EmailSignatureEditor() {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/auth/me')
      .then((res) => { if (!cancelled) setValue(res.data?.email_signature || ''); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function save() {
    setSaving(true); setError(null);
    try {
      await axios.put('/api/auth/me/signature', { email_signature: value });
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsCard
      title="Email Signature"
      subtitle="Appended to outgoing quotes, Bulloch requests, and other emails sent from your account."
    >
      {loading ? <p className="modal-sub">Loading…</p> : (
        <>
          {error && <p className="error">{error}</p>}
          <textarea
            className="form-input settings-signature"
            rows={6}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Your signature"
          />
          <div className="settings-card-actions">
            <button className="btn-primary" onClick={save} disabled={saving}>
              <Save size={14} strokeWidth={2} /> {saving ? 'Saving…' : 'Save Signature'}
            </button>
            {savedAt && <span className="save-message">Saved.</span>}
          </div>
        </>
      )}
    </SettingsCard>
  );
}

// ---- Administration -----------------------------------------------
function AdministrationPanel() {
  return (
    <>
      <SettingsCard
        title="Manage Users"
        subtitle="Add, edit, deactivate, or reset passwords for XSite Command users."
      >
        <div className="settings-card-actions">
          <Link to="/settings/users" className="btn-primary">Manage Users →</Link>
        </div>
      </SettingsCard>

      <SettingsCard
        title="Import History"
        subtitle="Review and undo recent CSV imports. Undo skips records that have been modified since import."
      >
        <div className="settings-card-actions">
          <Link to="/settings/imports" className="btn-primary">View Import History →</Link>
        </div>
      </SettingsCard>
    </>
  );
}

// ---- Integrations -------------------------------------------------
// QBO_ENABLED gates the QuickBooks Online connection panel. Set to
// false 2026-05-27 — Dave called off the integration because the
// per-tenant OAuth + rate limits + field-mapping mismatches were
// fighting daily workflow. Backend (server/controllers/qboController,
// /api/qbo/* routes) stays intact; flipping this to true is the
// only thing needed to bring the UI back if we ever revisit.
// Re-enabled 2026-10-23 for the A/R QuickBooks sync (open invoices only).
// The Customers-page customer sync stays OFF (its own flag in
// Customers.jsx): it creates new contacts for unlinked QBO customers.
const QBO_ENABLED = true;
function IntegrationsPanel() {
  return (
    <>
      {QBO_ENABLED && <QboConnection />}
      <SettingsCard
        title="XSite Back Office"
        subtitle="Authentication for the external subscription system at backend.xsiteonline.com."
      >
        <div className="settings-status-row">
          <span className="badge badge-amber">PLACEHOLDER</span>
          <span className="form-hint">
            Auto-login is being set up. For now, the Back Office token lives in the server's <code>.env</code> file
            and must be refreshed manually when it expires (~every 24 hours).
          </span>
        </div>
      </SettingsCard>
    </>
  );
}

// ---- QuickBooks Online connection ---------------------------------
// Manages the OAuth link between XSite Command and the user's QBO
// company. Hits the backend at /api/qbo/* — see server/controllers/
// qboController.js for the flow.
function QboConnection() {
  const [status, setStatus] = useState(null);   // null = loading; object = loaded
  const [error, setError]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [flash, setFlash]   = useState(null);   // { type: 'success' | 'info' | 'error', text }

  // Handle the ?qbo=connected / ?qbo=denied query params that the
  // server adds after the OAuth callback redirects back here.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qbo = params.get('qbo');
    if (qbo === 'connected') {
      setFlash({ type: 'success', text: 'Connected to QuickBooks Online.' });
    } else if (qbo === 'denied') {
      setFlash({ type: 'info', text: 'QuickBooks connection cancelled — no changes made.' });
    }
    if (qbo) {
      // Strip the query param so a page refresh doesn't keep replaying the flash.
      const url = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', url);
    }
  }, []);

  async function loadStatus() {
    setError(null);
    try {
      const r = await axios.get('/api/qbo/status');
      setStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStatus({ connected: false });
    }
  }
  useEffect(() => { loadStatus(); }, []);

  function connect() {
    // Full-page navigation rather than fetch — the server responds with
    // an HTTP redirect to Intuit's consent screen, which the browser
    // needs to follow as a top-level navigation.
    window.location.href = '/api/qbo/connect';
  }

  async function disconnect() {
    if (!window.confirm(
      'Disconnect XSite Command from QuickBooks?\n\n' +
      'This removes the saved access tokens but does NOT delete any customer data. ' +
      'You can reconnect anytime.'
    )) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete('/api/qbo/disconnect');
      await loadStatus();
      setFlash({ type: 'info', text: 'Disconnected from QuickBooks.' });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsCard
      title="QuickBooks Online"
      subtitle="Read-only link to QuickBooks Online. Accounts Receivable uses it to pull open invoices and balances; nothing is written back to QuickBooks."
    >
      {error && <p className="error">{error}</p>}
      {flash && (
        <p className={flash.type === 'success' ? 'save-message' : 'modal-sub'}>
          {flash.text}
        </p>
      )}

      {status === null ? (
        <p className="modal-sub">Loading…</p>
      ) : status.connected ? (
        <>
          <div className="settings-status-row">
            <span className="badge badge-green">CONNECTED</span>
            <span className="form-hint">
              QuickBooks company <code>{status.realmId}</code>
              {status.connectedAt && <> — last refreshed {formatDateTime(status.connectedAt)}</>}
              {status.refreshNeeded && (
                <> — <strong>refresh token expires soon, reconnect to keep the sync working</strong></>
              )}
            </span>
          </div>
          <div className="settings-card-actions">
            <button className="btn-secondary" onClick={disconnect} disabled={busy}>
              <Link2Off size={14} strokeWidth={2} /> {busy ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="settings-status-row">
            <span className="badge badge-grey">NOT CONNECTED</span>
            <span className="form-hint">
              Connect to your QuickBooks Online company to enable the A/R sync.
            </span>
          </div>
          <div className="settings-card-actions">
            <button className="btn-primary" onClick={connect} disabled={busy}>
              <Link2 size={14} strokeWidth={2} /> Connect to QuickBooks
            </button>
          </div>
        </>
      )}
    </SettingsCard>
  );
}

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

// ---- Data Lists ---------------------------------------------------
function DataListsPanel() {
  return (
    <>
      <FuelBrandsManager />
      <CcGroupsManager />
      <PosTypesManager />
      <EmailTemplatesManager />
      <SettingsCard
        collapsible
        title="Credential Templates"
        subtitle="SFTP defaults (host, port, username, directories, file types, run time) for Esso, PetroCan, and Passport. Changes become the new defaults wherever that credential type is selected."
      >
        <div className="settings-card-actions">
          <Link to="/settings/credential-templates" className="btn-primary">
            Edit Credential Templates →
          </Link>
        </div>
      </SettingsCard>
    </>
  );
}

// ---- Email Templates manager ---------------------------------------
// CRUD over /api/email-templates. Templates show up in the Send Email
// dialog on lead detail pages — this is where Dave + Mary maintain the
// canonical list. Supports the merge field set defined in
// SendEmailDialog.renderTemplate: {{first_name}}, {{last_name}},
// {{company}}, {{user_name}}, {{user_title}}, {{user_email}}.
const TEMPLATE_MERGE_FIELDS = '{{first_name}}, {{last_name}}, {{company}}, {{user_name}}, {{user_title}}, {{user_email}}';

function emptyTemplateDraft() {
  return { name: '', category: '', subject: '', body: '' };
}

function EmailTemplatesManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // editingId === null  → list view; editingId === 'new' → create form
  // editingId === <num> → editing that template
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyTemplateDraft);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get('/api/email-templates');
      setTemplates(r.data.templates || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startNew() {
    setDraft(emptyTemplateDraft());
    setEditingId('new');
    setError(null);
  }

  function startEdit(t) {
    setDraft({
      name:     t.name     || '',
      category: t.category || '',
      subject:  t.subject  || '',
      body:     t.body     || ''
    });
    setEditingId(t.id);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setDraft(emptyTemplateDraft());
  }

  async function save() {
    if (!draft.name.trim() || !draft.subject.trim() || !draft.body.trim()) {
      setError('Name, Subject and Body are all required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name:     draft.name,
        category: draft.category,
        subject:  draft.subject,
        body:     draft.body
      };
      if (editingId === 'new') {
        await axios.post('/api/email-templates', payload);
      } else {
        await axios.put(`/api/email-templates/${editingId}`, payload);
      }
      await load();
      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(t) {
    if (!window.confirm(
      `Delete email template "${t.name}"?\n\nThis removes it from the Send Email dialog. ` +
      `Past emails that used this template stay intact (the body was copied into each email at send time).`
    )) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`/api/email-templates/${t.id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsCard
      collapsible
      title="Email Templates"
      subtitle="Saved subject/body templates that show up in the Send Email dialog on lead pages. Merge fields like {{first_name}} and {{company}} fill in automatically from the lead's record at send time."
    >
      {error && <p className="error">{error}</p>}

      {editingId === null ? (
        <>
          <div className="settings-card-actions" style={{ marginBottom: 'var(--space-3)' }}>
            <button type="button" className="btn-primary btn-sm" onClick={startNew} disabled={busy}>
              <Plus size={14} strokeWidth={2} /> Add Template
            </button>
          </div>

          {loading && <p className="modal-sub">Loading…</p>}
          {!loading && templates.length === 0 && (
            <p className="empty-tab">No templates yet. Add one above.</p>
          )}
          {!loading && templates.length > 0 && (
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Subject</th>
                  <th style={{ width: 1, whiteSpace: 'nowrap' }}></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td className="cell-primary">{t.name}</td>
                    <td>
                      {t.category
                        ? <Badge tone="grey">{t.category}</Badge>
                        : <span className="cell-muted">—</span>}
                    </td>
                    <td className="cell-subtext">{t.subject}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => startEdit(t)}
                        disabled={busy}
                        title="Edit"
                        aria-label={`Edit ${t.name}`}
                      >
                        <Pencil size={14} strokeWidth={1.75} />
                      </button>{' '}
                      <button
                        type="button"
                        className="btn-secondary btn-sm fuel-brand-delete"
                        onClick={() => remove(t)}
                        disabled={busy}
                        title="Delete"
                        aria-label={`Delete ${t.name}`}
                      >
                        <Trash2 size={14} strokeWidth={1.75} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
        // ─── Edit / create form ────────────────────────────────────
        <div className="settings-template-editor" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <label className="form-field">
            <span>Name</span>
            <input
              type="text"
              className="form-input"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Web form follow-up — demo offer"
              maxLength={200}
              autoFocus
            />
          </label>

          <label className="form-field">
            <span>Category <span className="form-hint">(optional — groups templates in the Send Email dropdown)</span></span>
            <input
              type="text"
              className="form-input"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              placeholder="e.g. Sales, Customer follow-up, Quote"
              maxLength={100}
            />
          </label>

          <label className="form-field">
            <span>Subject</span>
            <input
              type="text"
              className="form-input"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              placeholder="Thanks for reaching out, {{first_name}} — let's set up a demo"
              maxLength={500}
            />
          </label>

          <label className="form-field">
            <span>Body</span>
            <textarea
              className="form-input"
              rows={10}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder={`Hi {{first_name}},\n\nThanks for getting in touch.\n\n…`}
              style={{ resize: 'vertical', minHeight: 200, fontFamily: 'var(--font-sans)' }}
            />
            <span className="form-hint" style={{ marginTop: 'var(--space-1)' }}>
              Available merge fields: <code>{TEMPLATE_MERGE_FIELDS}</code>. Your email signature is appended automatically at send time — don't paste it here.
            </span>
          </label>

          <div className="settings-card-actions">
            <button type="button" className="btn-primary" onClick={save} disabled={busy}>
              <Save size={14} strokeWidth={2} /> {busy ? 'Saving…' : (editingId === 'new' ? 'Create Template' : 'Save Changes')}
            </button>
            <button type="button" className="btn-secondary" onClick={cancelEdit} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </SettingsCard>
  );
}

// ---- Fuel Brands (lifted from the previous Settings.jsx, slotted
// into the new SettingsCard chrome — state/handlers unchanged) ------
function FuelBrandsManager() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/fuel-brands');
      setBrands(res.data.fuel_brands || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    const name = adding.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post('/api/fuel-brands', { name });
      setAdding('');
      await load();
      refreshFuelBrands();        // broadcast to live form dropdowns
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  function startEdit(b) {
    setEditingId(b.id);
    setEditingValue(b.name);
    setError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingValue('');
  }

  async function saveEdit() {
    const name = editingValue.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      await axios.put(`/api/fuel-brands/${editingId}`, { name });
      cancelEdit();
      await load();
      refreshFuelBrands();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(b) {
    if (!window.confirm(`Delete fuel brand "${b.name}"?\n\nExisting customers and sites already tagged with "${b.name}" keep that value — it just disappears from new-record dropdowns.`)) return;
    setBusy(true);
    setError(null);
    try {
      await axios.delete(`/api/fuel-brands/${b.id}`);
      await load();
      refreshFuelBrands();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsCard
      collapsible
      title="Fuel Brands"
      subtitle="Manage the list shown in Fuel Brand dropdowns on Customer, Lead and Site forms. Deleting a brand removes it from new dropdowns but leaves existing records' tagged values intact."
    >
      {error && <p className="error">{error}</p>}

      <form className="fuel-brand-add" onSubmit={handleAdd}>
        <input
          type="text"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="Add a brand (e.g. Husky)"
          disabled={busy}
          maxLength={50}
        />
        <button type="submit" className="btn-primary btn-sm" disabled={busy || !adding.trim()}>
          <Plus size={14} strokeWidth={2} />
          Add Brand
        </button>
      </form>

      <div className="fuel-brand-list">
        {loading && <p className="modal-sub">Loading…</p>}
        {!loading && brands.length === 0 && (
          <p className="empty-tab">No fuel brands on file. Add one above to get started.</p>
        )}
        {brands.map((b) => {
          const isEditing = editingId === b.id;
          return (
            <div key={b.id} className="fuel-brand-row">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    autoFocus
                    maxLength={50}
                  />
                  <div className="fuel-brand-actions">
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={saveEdit}
                      disabled={busy || !editingValue.trim()}
                      title="Save (Enter)"
                    >
                      <Check size={14} strokeWidth={2} />
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={cancelEdit}
                      disabled={busy}
                      title="Cancel (Esc)"
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="fuel-brand-name">{b.name}</span>
                  <div className="fuel-brand-actions">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => startEdit(b)}
                      disabled={busy}
                      title="Edit"
                      aria-label={`Edit ${b.name}`}
                    >
                      <Pencil size={14} strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-sm fuel-brand-delete"
                      onClick={() => handleDelete(b)}
                      disabled={busy}
                      title="Delete"
                      aria-label={`Delete ${b.name}`}
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </SettingsCard>
  );
}

// ---- CC Groups ----------------------------------------------------
function CcGroupsManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // row id
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(null);   // 'bulloch_only' | 'parkland' | null
  const [newRow, setNewRow] = useState({ contact_name: '', email: '', organization: '' });

  async function load() {
    setLoading(true); setError(null);
    try {
      const r = await axios.get('/api/cc-groups');
      setRows(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startEdit(row) {
    setEditing(row.id);
    setDraft({ contact_name: row.contact_name || '', email: row.email || '', organization: row.organization || '', active: row.active });
  }
  function cancelEdit() { setEditing(null); setDraft({}); }

  async function saveEdit() {
    try {
      await axios.put(`/api/cc-groups/${editing}`, draft);
      cancelEdit();
      await load();
    } catch (e) { setError(e.response?.data?.error || e.message); }
  }

  async function remove(row) {
    if (!window.confirm(`Remove ${row.email} from the ${groupLabel(row.group_name)} CC list?`)) return;
    try { await axios.delete(`/api/cc-groups/${row.id}`); await load(); }
    catch (e) { setError(e.response?.data?.error || e.message); }
  }

  async function saveNew(group) {
    if (!newRow.email.trim()) return;
    try {
      await axios.post('/api/cc-groups', { group_name: group, ...newRow });
      setAdding(null); setNewRow({ contact_name: '', email: '', organization: '' });
      await load();
    } catch (e) { setError(e.response?.data?.error || e.message); }
  }

  const grouped = useMemo(() => {
    const g = { bulloch_only: [], parkland: [] };
    for (const r of rows) (g[r.group_name] || (g[r.group_name] = [])).push(r);
    return g;
  }, [rows]);

  return (
    <SettingsCard
      collapsible
      title="CC Groups"
      subtitle="Recipient lists that get CC'd on Bulloch configuration request emails. Bulloch Only goes on Esso/Chevron/Mobil/Independent sites; Parkland goes on Ultramar/Fast Gas/Pioneer sites."
    >
      {error && <p className="error">{error}</p>}
      {loading ? <p className="modal-sub">Loading…</p> : (
        <>
          {['bulloch_only', 'parkland', 'petrocan_jim'].map((group) => (
            <div key={group} className="cc-group-block">
              <div className="cc-group-head">
                <h4>{groupLabel(group)}</h4>
                <button className="btn-secondary btn-sm" onClick={() => setAdding(group)}>
                  <Plus size={14} /> Add
                </button>
              </div>

              <table className="data-table cc-group-table">
                <thead>
                  <tr><th>Contact</th><th>Email</th><th>Organization</th><th>Active</th><th></th></tr>
                </thead>
                <tbody>
                  {grouped[group].map((row) => {
                    const isEditing = editing === row.id;
                    return (
                      <tr key={row.id}>
                        <td>{isEditing
                          ? <input className="form-input" value={draft.contact_name} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} />
                          : (row.contact_name || '—')}</td>
                        <td className="cell-mono">{isEditing
                          ? <input className="form-input" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
                          : row.email}</td>
                        <td>{isEditing
                          ? <input className="form-input" value={draft.organization} onChange={(e) => setDraft({ ...draft, organization: e.target.value })} />
                          : (row.organization || '—')}</td>
                        <td>{isEditing
                          ? <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
                          : (row.active ? <span className="badge badge-green">ACTIVE</span> : <span className="badge badge-grey">OFF</span>)}</td>
                        <td>
                          {isEditing ? (
                            <>
                              <button className="btn-primary btn-sm" onClick={saveEdit}><Check size={14} /></button>
                              <button className="btn-secondary btn-sm" onClick={cancelEdit}><X size={14} /></button>
                            </>
                          ) : (
                            <>
                              <button className="btn-secondary btn-sm" onClick={() => startEdit(row)}><Pencil size={14} /></button>
                              <button className="btn-secondary btn-sm" onClick={() => remove(row)}><Trash2 size={14} /></button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {adding === group && (
                    <tr className="cc-group-newrow">
                      <td><input className="form-input" placeholder="Name (optional)" value={newRow.contact_name} onChange={(e) => setNewRow({ ...newRow, contact_name: e.target.value })} /></td>
                      <td><input className="form-input" placeholder="email@domain.com" value={newRow.email} onChange={(e) => setNewRow({ ...newRow, email: e.target.value })} /></td>
                      <td><input className="form-input" placeholder="Organization (optional)" value={newRow.organization} onChange={(e) => setNewRow({ ...newRow, organization: e.target.value })} /></td>
                      <td>—</td>
                      <td>
                        <button className="btn-primary btn-sm" onClick={() => saveNew(group)} disabled={!newRow.email.trim()}><Check size={14} /></button>
                        <button className="btn-secondary btn-sm" onClick={() => { setAdding(null); setNewRow({ contact_name: '', email: '', organization: '' }); }}><X size={14} /></button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
    </SettingsCard>
  );
}

function groupLabel(g) {
  // Chevron moved into parkland 2026-07-09; petrocan_jim added same day
  // for PetroCan/Suncor sites (single-member group).
  if (g === 'parkland')      return 'Parkland (Ultramar / Fast Gas / Pioneer / Chevron)';
  if (g === 'petrocan_jim')  return 'PetroCan (Jim Schroder at Suncor)';
  return 'Bulloch Only (Esso / Mobil / Independent)';
}

// ---- POS Types (user-managed, mirrors Fuel Brands + a tone picker) -
function PosTypesManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState({ name: '', tone: 'blue' });
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({ name: '', tone: 'blue' });
  const [busy, setBusy] = useState(false);

  const TONES = ['blue', 'red', 'purple', 'orange', 'green', 'amber', 'grey', 'teal'];

  async function load() {
    setLoading(true); setError(null);
    try {
      const r = await axios.get('/api/pos-types');
      setRows(r.data.pos_types || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!adding.name.trim()) return;
    setBusy(true); setError(null);
    try {
      await axios.post('/api/pos-types', adding);
      setAdding({ name: '', tone: 'blue' });
      await load();
      refreshPosTypes();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  }

  function startEdit(r) { setEditingId(r.id); setEdit({ name: r.name, tone: r.tone }); }
  function cancelEdit() { setEditingId(null); }

  async function saveEdit() {
    setBusy(true); setError(null);
    try {
      await axios.put(`/api/pos-types/${editingId}`, edit);
      cancelEdit();
      await load();
      refreshPosTypes();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  }

  async function handleDelete(r) {
    if (!window.confirm(`Delete POS type "${r.name}"?\n\nExisting customers and sites tagged with "${r.name}" keep that value — it just disappears from new-record dropdowns.`)) return;
    setBusy(true); setError(null);
    try {
      await axios.delete(`/api/pos-types/${r.id}`);
      await load();
      refreshPosTypes();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(false); }
  }

  return (
    <SettingsCard
      collapsible
      title="POS Types"
      subtitle="The POS types shown on Customer, Lead and Site forms. Each gets a colored badge — pick a tone that fits the brand. Deleting a type removes it from new-record dropdowns but leaves existing records' tagged values intact."
    >
      {error && <p className="error">{error}</p>}

      <form className="fuel-brand-add" onSubmit={handleAdd}>
        <input
          type="text"
          value={adding.name}
          onChange={(e) => setAdding({ ...adding, name: e.target.value })}
          placeholder="Add a POS type (e.g. Bulloch Husky)"
          disabled={busy}
          maxLength={50}
        />
        <select
          value={adding.tone}
          onChange={(e) => setAdding({ ...adding, tone: e.target.value })}
          disabled={busy}
          className="form-input"
          style={{ maxWidth: 120 }}
        >
          {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="submit" className="btn-primary btn-sm" disabled={busy || !adding.name.trim()}>
          <Plus size={14} /> Add
        </button>
      </form>

      <div className="fuel-brand-list">
        {loading && <p className="modal-sub">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="empty-tab">No POS types yet. Add one above.</p>
        )}
        {rows.map((r) => {
          const isEditing = editingId === r.id;
          return (
            <div key={r.id} className="fuel-brand-row">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    value={edit.name}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') cancelEdit(); }}
                    autoFocus
                    maxLength={50}
                  />
                  <select
                    value={edit.tone}
                    onChange={(e) => setEdit({ ...edit, tone: e.target.value })}
                    className="form-input"
                    style={{ maxWidth: 120 }}
                  >
                    {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <div className="fuel-brand-actions">
                    <button type="button" className="btn-primary btn-sm" onClick={saveEdit} disabled={busy || !edit.name.trim()}><Check size={14} /></button>
                    <button type="button" className="btn-secondary btn-sm" onClick={cancelEdit} disabled={busy}><X size={14} /></button>
                  </div>
                </>
              ) : (
                <>
                  <span className="fuel-brand-name" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <Badge tone={r.tone}>{r.name}</Badge>
                    <span style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>tone: {r.tone}</span>
                  </span>
                  <div className="fuel-brand-actions">
                    <button type="button" className="btn-secondary btn-sm" onClick={() => startEdit(r)} disabled={busy} title="Edit"><Pencil size={14} /></button>
                    <button type="button" className="btn-secondary btn-sm fuel-brand-delete" onClick={() => handleDelete(r)} disabled={busy} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </SettingsCard>
  );
}

// ---- Reusable card wrapper ----------------------------------------
function SettingsCard({ title, subtitle, collapsible = false, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;

  return (
    <section className={`settings-card${collapsible ? ' settings-card-collapsible' : ''}${isOpen ? ' settings-card-open' : ''}`}>
      <header
        className="settings-card-head"
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        onKeyDown={collapsible ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
        } : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
      >
        {collapsible && (
          <ChevronRight
            size={16}
            strokeWidth={2}
            className={`settings-card-chevron${isOpen ? ' settings-card-chevron-open' : ''}`}
          />
        )}
        <div className="settings-card-head-text">
          <h3>{title}</h3>
          {subtitle && <p className="settings-card-sub">{subtitle}</p>}
        </div>
      </header>
      {isOpen && <div className="settings-card-body">{children}</div>}
    </section>
  );
}
